/**
 * stat-count-up.js — 方向A「精密儀器」chunk4 巧思②：dashboard 統計數字 count-up 進場
 *
 * 效果：.stat-card__value 的數字由 0 滾動到目標值（ease-out 數值曲線，非 linear），
 *   同步「數字本體」fade-up（translateY(--motion-count-distance 20px →0) + opacity 0→1）。
 *   ⚠ 卡片框（.stat-card）不淡入——框先在、數字後跳（task 規格：容器不淡入）。故位移/透明度只套在
 *   value 元素本身（JS inline 管理），不碰卡片框的既有進場（前台 scroll-reveal / 後台 adminReveal）。
 *
 * 解耦（不動任何 reveal 核心）：本檔自帶獨立 IntersectionObserver，一次性觸發。
 *   - 不依賴 .is-revealed（前台 scroll-reveal.js 加）也不依賴 adminReveal（後台 admin.css 載入即播）。
 *   - 統計卡現役全在後台/會員端（admin.js 入口，無 scroll-reveal.js），故不能 key off .is-revealed；
 *     獨立 IO 對前台/後台兩種 reveal 機制都一致可用，零耦合、零互搶。
 *   - 數字本身 fade-up 由本檔 inline transform/opacity 管理，與卡片框 reveal 是兩個獨立來源、不疊壓
 *     （框動框的、數字動數字的）。
 *
 * 目標篩選：只挑 .stat-card__value 且 textContent 去逗號後為純數字者（parseTarget 回傳非 null）。
 *   非數值（理論上現況皆整數，防禦性保留）跳過、原樣不動。
 *
 * Fallback：
 *   - no-JS：HTML 由 _stat-card.html 預填最終值（value 逐字輸出）→ JS 停用即顯終值。本檔啟動才先歸 0 再計數。
 *   - reduced-motion：完全跳過計數與 fade-up，數字維持 HTML 預填終值、transform 不設（框先在數字也在）。
 *
 * a11y：value 文字在計數中快速變動，但 .stat-card__value 非 aria-live region（無 aria-live），
 *   螢幕報讀不會逐格朗讀中間值；可點卡的無障礙名來自 aria-label、doctors 唯讀卡來自 aria-labelledby→
 *   報讀取的是「快照當下」文字，計數極短（600ms）且最終為真值，不影響無障礙語意。不加 aria-live。
 *
 * 載入：由 /src/admin.js（後台/會員端入口，統計卡所在）中央 import 鏈 auto-init。
 *   若也被 main.js 載入：前台無 .stat-card__value → querySelectorAll 空集合 → 實質 no-op，無害。
 */

const PRM = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const VALUE_SELECTOR = '.stat-card__value';

/* ease-out cubic 數值曲線（對應 --ease-count-up cubic-bezier(0.4,0,0.2,1) 的「快起慢收」族）。
   數值插值用 cubic ease-out（1-(1-t)^3）—— 與 CSS 同調的 decelerate 手感，非 linear 等速。 */
const easeOut = (t) => 1 - Math.pow(1 - t, 3);

/* token 讀取：把 motion.css 的 --motion-count-up(600ms) / --motion-count-distance(20px) 讀進 JS。
   單一真相源仍是 motion.css；此處解析其值供 rAF 計時與初始位移用，不在 JS 內重定義數字。 */
const readMs = (cssVar, fallback) => {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(cssVar).trim();
  const m = raw.match(/([\d.]+)\s*(ms|s)?/);
  if (!m) return fallback;
  const n = parseFloat(m[1]);
  return m[2] === 's' ? n * 1000 : n;
};

const COUNT_UP_MS = readMs('--motion-count-up', 600);
const COUNT_DISTANCE = (getComputedStyle(document.documentElement)
  .getPropertyValue('--motion-count-distance').trim() || '20px');

/**
 * 解析目標數字：去千分位逗號後須為純數字字串（保留原小數位數與格式骨架）。
 * 回傳 { target, decimals } 或 null（非數值 → 呼叫端跳過）。
 */
const parseTarget = (text) => {
  const cleaned = text.replace(/,/g, '').trim();
  if (cleaned === '' || !/^\d+(\.\d+)?$/.test(cleaned)) return null;
  const decimals = cleaned.includes('.') ? cleaned.split('.')[1].length : 0;
  return { target: parseFloat(cleaned), decimals };
};

/**
 * 依原文是否含千分位逗號，格式化計數中的中間值（維持與終值一致的呈現）。
 */
const formatNum = (val, decimals, useGrouping) => {
  const fixed = val.toFixed(decimals);
  if (!useGrouping) return fixed;
  const [intPart, decPart] = fixed.split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return decPart !== undefined ? `${grouped}.${decPart}` : grouped;
};

/**
 * 對單一 value 元素跑一次 count-up + fade-up（rAF 驅動，ease-out 數值曲線）。
 */
const runCountUp = (el) => {
  const finalText = el.textContent;
  const parsed = parseTarget(finalText);
  if (!parsed) return; // 非數值 → 不動

  const { target, decimals } = parsed;
  const useGrouping = finalText.includes(',');

  // 起點：歸 0 + 數字下移一段（fade-up 起點），框不動。
  el.style.transform = `translateY(${COUNT_DISTANCE})`;
  el.style.opacity = '0';
  el.style.willChange = 'transform, opacity';
  el.textContent = formatNum(0, decimals, useGrouping);

  const start = performance.now();

  const tick = (now) => {
    const elapsed = now - start;
    const progress = Math.min(elapsed / COUNT_UP_MS, 1);
    const eased = easeOut(progress);

    // 數字：0→target（ease-out）
    el.textContent = formatNum(target * eased, decimals, useGrouping);
    // fade-up：translateY(distance→0) + opacity(0→1)，與計數同步同曲線
    const remaining = (1 - eased);
    el.style.transform = `translateY(calc(${COUNT_DISTANCE} * ${remaining}))`;
    el.style.opacity = String(eased);

    if (progress < 1) {
      requestAnimationFrame(tick);
    } else {
      // 收尾定格：寫回 HTML 原文（避免 toFixed 抹掉非數字格式骨架）、清 inline 樣式回 CSS 預設
      el.textContent = finalText;
      el.style.transform = '';
      el.style.opacity = '';
      el.style.willChange = '';
    }
  };

  requestAnimationFrame(tick);
};

/* ---------- init ---------- */

const values = document.querySelectorAll(VALUE_SELECTOR);

// reduced-motion / 老瀏覽器 / 無統計卡：不觀察、不計數、不位移 → 數字維持 HTML 預填終值
// （框先在、數字也在），用 CSS 預設（value 本就 opacity:1）。直接早退、零副作用。
if (values.length && !PRM && 'IntersectionObserver' in window) {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        observer.unobserve(entry.target); // 一次性
        runCountUp(entry.target);
      }
    });
  }, {
    threshold: 0.4,
    rootMargin: '0px 0px -8% 0px',
  });

  values.forEach((el) => observer.observe(el));
}
